import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Lightbulb, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useServiceMap } from "@/lib/data";
import { ServiceIcon } from "@/lib/icons";
import { ServiceSearchBox } from "@/components/ServiceSearch";

const MAX = 5;

function Suggest() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const { data: mine = [] } = useQuery({ queryKey: ["my-suggestions"], queryFn: () => api.get("/service-suggestions/mine").then((r) => r.data) });
  const send = async () => {
    try { await api.post("/service-suggestions", { name }); toast.success("Sugestão enviada! O administrador vai analisar."); setName(""); setOpen(false); qc.invalidateQueries({ queryKey: ["my-suggestions"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pending = mine.filter((s) => s.status === "pending");
  return (
    <div className="mt-3">
      {pending.map((s) => <p key={s.id} className="mb-2 flex items-center gap-2 rounded-2xl bg-yellow-50 p-3 text-base text-yellow-900" data-testid="pending-suggestion"><Clock className="h-5 w-5" /> Sugestão “{s.name}” em análise</p>)}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="text-lg font-semibold text-orange-600 underline" data-testid="not-found-service-button">
          <Lightbulb className="inline h-5 w-5" /> Não achei meu serviço
        </button>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-orange-300 p-4">
          <label className="po-label" htmlFor="sug-name">Qual é o seu serviço?</label>
          <input id="sug-name" className="po-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Instalador de rede de proteção" data-testid="suggestion-name-input" />
          <p className="mt-1 text-base text-slate-600">O administrador vai analisar e adicionar ao seu cadastro.</p>
          <div className="mt-3 flex gap-2">
            <button type="button" disabled={name.trim().length < 3} onClick={send} className="po-btn-primary flex-1" data-testid="suggestion-submit-button">Enviar sugestão</button>
            <button type="button" onClick={() => setOpen(false)} className="po-btn-outline">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProviderServicesPick({ value, primary, onChange }) {
  const services = useServiceMap();
  const add = (s) => {
    if (value.includes(s.slug)) return;
    if (value.length >= MAX) return toast.error(`Você pode escolher até ${MAX} serviços.`);
    const next = [...value, s.slug];
    onChange(next, primary || s.slug);
  };
  const remove = (slug) => {
    const next = value.filter((x) => x !== slug);
    onChange(next, next.includes(primary) ? primary : next[0] || "");
  };
  return (
    <div>
      <p className="po-label">Quais serviços você faz? (até {MAX})</p>
      <div className="mb-3 flex flex-wrap gap-2" data-testid="pf-selected-services">
        {value.map((slug) => (
          <span key={slug} className="inline-flex items-center gap-2 rounded-full bg-orange-500 py-1.5 pl-4 pr-1.5 text-base font-bold text-white" data-testid={`pf-selected-${slug}`}>
            <ServiceIcon icon={services[slug]?.icon} className="h-5 w-5" /> {services[slug]?.name || slug}
            <button type="button" onClick={() => remove(slug)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30" aria-label="Remover"><X className="h-4 w-4" /></button>
          </span>
        ))}
        {!value.length && <span className="text-base text-slate-600">Nenhum serviço escolhido ainda.</span>}
      </div>
      {value.length < MAX && <ServiceSearchBox onPick={add} selected={value} keepOpen testId="pf-service-search" placeholder="Digite seu serviço para buscar" />}
      <Suggest />
      {value.length > 1 && (
        <div className="mt-4">
          <label className="po-label" htmlFor="pf-primary">Serviço principal (ícone no mapa)</label>
          <select id="pf-primary" className="po-select" value={primary} onChange={(e) => onChange(value, e.target.value)} data-testid="pf-primary-select">
            {value.map((s) => <option key={s} value={s}>{services[s]?.name || s}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
